// The swing picker, Guitar Pro's "triplet feel" choices as two rows of pills
// (user, Oct 2026: the first grid of pictures "is massive", then "put the
// notation back in"): how the pair is split, each pill showing the pair as it
// is played, then which notes swing, each showing the written pair. The split
// pictures follow the 8ths/16ths choice, so the pills always draw what the
// band will play. Drawn in currentColor, so they read on every theme.

import { Drum } from 'lucide-react';
import type { SwingChoice } from '@/music/swingFeel';
import { SegmentedChoice } from './MixerControls';
import { SwingRatioSlider } from './SwingRatioSlider';
import {
  composeSwing,
  SWING_LEVEL_CHOICES,
  SWING_RATIO_CHOICES,
  swingLevelOf,
  swingRatioOf,
  type SwingLevel,
  type SwingRatio,
} from './swingChoices';

/** One note of a drawn pair: how many beams (0 a quarter), dotted, flagged. */
interface DrawnNote {
  beams: number;
  dot?: boolean;
  flag?: boolean;
}

interface DrawnPair {
  notes: [DrawnNote, DrawnNote];
  triplet?: boolean;
}

/** How a pair is played under each split, at the eighth level; a sixteenth
 * pair is the same rhythm with one beam more. */
const PLAYED: Record<Exclude<SwingRatio, 'kit'>, DrawnPair> = {
  straight: { notes: [{ beams: 1 }, { beams: 1 }] },
  triplet: { notes: [{ beams: 0 }, { beams: 1, flag: true }], triplet: true },
  dotted: { notes: [{ beams: 1, dot: true }, { beams: 2 }] },
  scottish: { notes: [{ beams: 2 }, { beams: 1, dot: true }] },
};

/** The pair as played at a level: a sixteenth pair halves every value. */
function playedPair(ratio: Exclude<SwingRatio, 'kit'>, level: SwingLevel): DrawnPair {
  const pair = PLAYED[ratio];
  if (level === '8th') return pair;
  return {
    triplet: pair.triplet,
    notes: pair.notes.map((n) => ({ beams: n.beams + 1, dot: n.dot })) as [DrawnNote, DrawnNote],
  };
}

const writtenPair = (level: SwingLevel): DrawnPair => {
  const beams = level === '16th' ? 2 : 1;
  return { notes: [{ beams }, { beams }] };
};

const X = 3.5;
const HEAD_Y = 25;
const STEM_TOP = 8;
const NOTE_GAP = 10;
const STEM_DX = 2.35;
const BEAM_H = 1.9;
const BEAM_STEP = 3;
const STUB = 4;

/** Two notes, stems up, beamed when both carry a beam. */
export function SwingNotes({ pair }: { pair: DrawnPair }) {
  const { notes, triplet } = pair;
  const heads = notes.map((_, i) => X + i * NOTE_GAP);
  const stems = heads.map((h) => h + STEM_DX);
  const beamed = notes.every((n) => n.beams > 0 && !n.flag);
  const shared = beamed ? Math.min(...notes.map((n) => n.beams)) : 0;
  const parts: React.ReactNode[] = [];
  notes.forEach((note, i) => {
    parts.push(
      <ellipse
        key={`h${i}`}
        cx={heads[i]}
        cy={HEAD_Y}
        rx={2.6}
        ry={1.85}
        transform={`rotate(-22 ${heads[i]} ${HEAD_Y})`}
      />,
      <line
        key={`s${i}`}
        x1={stems[i]}
        x2={stems[i]}
        y1={HEAD_Y - 0.6}
        y2={STEM_TOP}
        stroke="currentColor"
        strokeWidth={0.9}
      />,
    );
    if (note.dot)
      parts.push(<circle key={`d${i}`} cx={heads[i] + 4.6} cy={HEAD_Y - 0.6} r={0.95} />);
    if (note.flag) {
      const sx = stems[i];
      parts.push(
        <path
          key={`f${i}`}
          d={`M ${sx} ${STEM_TOP} C ${sx + 0.4} ${STEM_TOP + 3} ${sx + 4.6} ${STEM_TOP + 4} ${sx + 3.4} ${STEM_TOP + 9.5} C ${sx + 3.6} ${STEM_TOP + 6} ${sx + 1.2} ${STEM_TOP + 5} ${sx} ${STEM_TOP + 4} Z`}
        />,
      );
    }
  });
  if (beamed) {
    for (let level = 0; level < 3; level++) {
      const y = STEM_TOP + level * BEAM_STEP;
      if (level < shared) {
        parts.push(
          <rect
            key={`b${level}`}
            x={stems[0] - 0.45}
            y={y}
            width={stems[1] - stems[0] + 0.9}
            height={BEAM_H}
          />,
        );
        continue;
      }
      // A level only one note carries: a short stub pointing at its partner.
      notes.forEach((note, i) => {
        if (note.beams <= level) return;
        const from = i === 0 ? stems[0] - 0.45 : stems[1] - STUB;
        parts.push(
          <rect key={`b${level}-${i}`} x={from} y={y} width={STUB + 0.45} height={BEAM_H} />,
        );
      });
    }
  }
  if (triplet) {
    const left = heads[0] - 2.4;
    const right = stems[1] + 0.5;
    const mid = (left + right) / 2;
    parts.push(
      <path
        key="tb"
        d={`M ${left} 5.6 V 3.4 H ${mid - 2} M ${mid + 2} 3.4 H ${right} V 5.6`}
        fill="none"
        stroke="currentColor"
        strokeWidth={0.7}
      />,
      <text
        key="t3"
        x={mid}
        y={5}
        textAnchor="middle"
        fontSize={5.2}
        fontStyle="italic"
        fontWeight={700}
      >
        3
      </text>,
    );
  }
  return (
    <svg
      className="swing-notes"
      viewBox="0 0 21.5 28"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      {parts}
    </svg>
  );
}

/** The band's swing against its kit, for the slider under Kit. */
export interface SwingAmount {
  /** The kit's ratio, the triplet when there is none: where 0 sits. */
  kit: number;
  /** The ratios the slider spans (swingFeel.ts `swingRatioRange`). */
  range: readonly [number, number];
  /** The band's stored offset from the kit. */
  offset: number;
  onChange: (offset: number) => void;
}

/** `value` is null when the bands being edited disagree: nothing lights.
 *  `kitStatus` names what the Kit choice swings at (swingChoices.ts), and
 *  `amount` is the band's swing against that kit, a slider under Kit. */
export function SwingFeelPicker({
  value,
  onChange,
  kitStatus,
  amount,
}: {
  value: SwingChoice | null;
  onChange: (choice: SwingChoice) => void;
  kitStatus?: string | null;
  amount?: SwingAmount | null;
}) {
  const ratio = value === null ? null : swingRatioOf(value);
  const level = value === null ? null : swingLevelOf(value);
  const drawnLevel = level ?? '8th';
  return (
    <>
      <div className="practice-feel-row swing-feel-row" data-testid="swing-feel">
        <span className="label">Swing</span>
        <SegmentedChoice
          label="Swing"
          value={ratio}
          options={SWING_RATIO_CHOICES.map((c) => ({
            ...c,
            // The kit swings where its drummer plays, which no notation draws.
            icon:
              c.id === 'kit' ? (
                <Drum className="swing-kit-icon" size={22} strokeWidth={1.6} />
              ) : (
                <SwingNotes pair={playedPair(c.id, drawnLevel)} />
              ),
          }))}
          onChange={(next) => onChange(composeSwing(next, level))}
          testid="swing-feel"
          className="swing-ratio swing-pictured"
        />
        <SegmentedChoice
          label="Swing notes"
          value={level}
          options={SWING_LEVEL_CHOICES.map((c) => ({
            ...c,
            icon: <SwingNotes pair={writtenPair(c.id)} />,
          }))}
          onChange={(next) => onChange(composeSwing(ratio === 'straight' ? null : ratio, next))}
          testid="swing-level"
          className="swing-level swing-pictured"
          // The kit's swing is an eighth swing.
          disabled={ratio === 'straight' || ratio === 'kit'}
        />
      </div>
      {value === 'kit' && amount && (
        <div className="practice-feel-row swing-amount-row" data-testid="swing-amount">
          <span className="label">Amount</span>
          <SwingRatioSlider
            base={amount.kit}
            range={amount.range}
            offset={amount.offset}
            onChange={amount.onChange}
            label="Band swing"
            testid="swing-amount-slider"
          />
        </div>
      )}
      {kitStatus && (
        <p className="swing-kit-status" data-testid="swing-kit-status">
          {kitStatus}
        </p>
      )}
    </>
  );
}
